import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { RateTrend } from '../components/RateTrend'
import { RfqForm } from '../components/RfqForm'
import { SponsorSlot } from '../components/SponsorSlot'
import { StarButton } from '../components/StarButton'
import { WatchLaneButton } from '../components/WatchLaneButton'
import { tradeById, tradeBucket, tradeName } from '../data/trades'
import { T, useLocale, useT } from '../i18n'
import { carrierBrand } from '../lib/carriers'
import { localesForPod } from '../lib/geo'
import { recordRecent } from '../lib/personal'
import { usePageMeta } from '../lib/meta'
import {
  type HistoryPoint,
  type LaneReliability,
  type LaneRow,
  type Sailing,
  type TransshipLeg,
  cleanPort,
  fmtPrice,
  getLaneHistory,
  getLaneReliability,
  getLaneSailings,
  getLanes,
  laneHeadline,
  laneSlug,
  parseLaneSlug,
} from '../lib/rates'

const cap = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase())
const CTR_KEY: Record<string, string> = { '20gp': '20GP', '40gp': '40GP', '40hc': '40HC' }
const CTR_LABEL: Record<string, string> = { '20GP': '20ft', '40GP': '40ft', '40HC': '40ft HC' }
// The chosen container's price on a sailing — so picking a box in the price cards flows the whole page
// (hero, schedule, same-ship comparison, pre-book) to that box, not just 40HC.
const priceForCtr = (
  s: Pick<Sailing, 'price_20gp' | 'price_40gp' | 'price_40hc'>,
  key: string
): number | null => (key === '20GP' ? s.price_20gp : key === '40GP' ? s.price_40gp : s.price_40hc)

// A specific sailing the shopper picked (a schedule row's Book, or a carrier price chip).
interface SelectedSailing {
  carrier: string | null
  vessel: string | null
  voyage: string | null
  etd: string | null
  price: number | null
  container: string
}

// On-time band → colour classes (literal strings so Tailwind keeps them). ≥70 good / 55–69 mid / poor.
const BAND = (p: number) =>
  p >= 70
    ? { text: 'text-up', bar: 'bg-up' }
    : p >= 55
      ? { text: 'text-signal', bar: 'bg-signal' }
      : { text: 'text-down', bar: 'bg-down' }

// On-time badge coloured by band.
function OnTime({ pct, obs }: { pct: number; obs?: number }) {
  return (
    <span className="tnum inline-flex items-baseline gap-1">
      <span className={`font-semibold ${BAND(pct).text}`}>{pct}%</span>
      {obs != null && <span className="text-[10px] text-faint">({obs})</span>}
    </span>
  )
}

// Every carrier we've analysed on this lane — the trust builder ("based on the last N sailings").
function ReliabilityPanel({ rel, sailings }: { rel: LaneReliability[]; sailings: Sailing[] }) {
  const t = useT()
  const vessels = new Set(sailings.map((s) => s.vessel).filter(Boolean)).size
  const lines = new Set(sailings.map((s) => s.carrier).filter(Boolean)).size
  const rows = [...rel].sort((a, b) => b.observations - a.observations)
  const totalObs = rows.reduce((s, r) => s + (r.observations || 0), 0)
  const relCarriers = new Set(rows.map((r) => r.carrier))
  const alsoSailing = [
    ...new Set(sailings.map((s) => s.carrier).filter(Boolean) as string[]),
  ].filter((c) => !relCarriers.has(c))

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold">{t('lane.relTitle')}</h2>
        <span className="mono text-[10px] uppercase tracking-wide text-faint">
          {t('lane.relSource')}
        </span>
      </div>
      <p className="mt-1.5 text-sm text-muted">
        {t('lane.relCoverage', { vessels: vessels || lines, lines })}
        {rows.length > 0 && ` ${t('lane.relAnalyzed', { obs: totalObs, carriers: rows.length })}`}
      </p>
      {rows.length === 0 ? (
        <p className="mono mt-4 text-xs text-faint">{t('lane.relBuilding')}</p>
      ) : (
        <div className="mt-4 space-y-3">
          {rows.map((r) => {
            const b = BAND(r.on_time_pct)
            return (
              <div
                key={r.carrier}
                className="grid grid-cols-[56px_1fr_auto] items-center gap-3 sm:grid-cols-[64px_1fr_auto]"
              >
                <span className="mono text-[13px] font-semibold text-ink">{r.carrier}</span>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
                  <div
                    className={`h-full rounded-full ${b.bar}`}
                    style={{ width: `${Math.max(3, r.on_time_pct)}%` }}
                  />
                </div>
                <span className="whitespace-nowrap text-right">
                  <span className={`mono text-[13px] font-semibold ${b.text}`}>
                    {r.on_time_pct}%
                  </span>
                  <span className="mono ml-2 text-[11px] text-faint">
                    {t('lane.relBasedOn', { n: r.observations })}
                  </span>
                </span>
              </div>
            )
          })}
        </div>
      )}
      {alsoSailing.length > 0 && (
        <p className="mt-4 text-xs text-muted">
          <span className="text-faint">{t('lane.relAlso')}:</span>{' '}
          {alsoSailing.slice(0, 8).join(', ')}{' '}
          <span className="text-faint">({t('lane.relTracking')})</span>
        </p>
      )}
    </div>
  )
}

// ── date helpers ──────────────────────────────────────────────────────────────
const D = (v: string | number | null | undefined) => {
  if (v == null || v === 0) return null
  const d = typeof v === 'number' ? new Date(v) : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}
const fmtDate = (v: string | number | null | undefined) =>
  D(v)?.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' }) ?? '—'
const fmtDateTime = (v: string | number | null | undefined) => {
  const d = D(v)
  return d
    ? d.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'
}

export function LanePage() {
  const t = useT()
  const locale = useLocale()
  const { slug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const ctr = (params.get('c') ?? '40hc').toLowerCase()
  const ctrKey = CTR_KEY[ctr] ?? '40HC'
  const pickCtr = (key: string) => setParams({ c: key.toLowerCase() }, { replace: true })
  const parsed = parseLaneSlug(slug)
  const pol = parsed?.pol
  const pod = parsed?.pod
  const [sailings, setSailings] = useState<Sailing[]>([])
  const [total, setTotal] = useState(0)
  const [rel, setRel] = useState<LaneReliability[]>([])
  const [history, setHistory] = useState<HistoryPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [suggest, setSuggest] = useState<LaneRow[]>([])
  const [emptyNames, setEmptyNames] = useState<{ pol?: string; pod?: string }>({})

  useEffect(() => {
    if (!pol || !pod) {
      setLoading(false)
      return
    }
    getLaneSailings(pol, pod)
      .then((lane) => {
        setSailings(lane.sailings)
        setTotal(lane.total)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
    getLaneReliability(pol, pod)
      .then(setRel)
      .catch(() => {})
    getLaneHistory(pol, pod)
      .then(setHistory)
      .catch(() => {})
  }, [pol, pod])

  // Smart empty state: if this lane has no live rate, fetch alternatives (same origin first).
  useEffect(() => {
    if (loading || sailings.length > 0) return
    getLanes()
      .then((all) => {
        const same = all.filter((l) => l.pol_code === pol && l.pod_code !== pod)
        setSuggest((same.length ? same : all).slice(0, 6))
        setEmptyNames({
          pol: all.find((l) => l.pol_code === pol)?.pol_name ?? undefined,
          pod: all.find((l) => l.pod_code === pod)?.pod_name ?? undefined,
        })
      })
      .catch(() => {})
  }, [loading, sailings.length, pol, pod])

  const relByCarrier = new Map(rel.map((r) => [r.carrier, r]))

  const { featured, updatedAt } = laneHeadline(sailings, ctrKey)

  // Which sailing the shopper clicked (Book on a schedule row, or a carrier price chip in the "same
  // ship" comparison). Prefills the pre-book panel so it's precise about the vessel/date/carrier/price.
  const [selected, setSelected] = useState<SelectedSailing | null>(null)
  function pickSailing(s: SelectedSailing) {
    setSelected(s)
    if (typeof document !== 'undefined')
      document.getElementById('book')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Remember this lane for the homepage "recently viewed" strip.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the lane identity only
  useEffect(() => {
    if (featured)
      recordRecent({
        pol: featured.pol_code,
        pod: featured.pod_code,
        polName: featured.pol_name,
        podName: featured.pod_name,
      })
  }, [featured?.pol_code, featured?.pod_code])

  // A lane exists in English + every locale whose country page claims its destination gateway.
  const availableLocales = featured
    ? ['en', ...localesForPod(featured.pod_code, featured.pod_country)]
    : ['en']
  const title = featured
    ? t('meta.lane.title', {
        pol: cleanPort(featured.pol_name, featured.pol_code),
        pod: cleanPort(featured.pod_name, featured.pod_code),
        price: fmtPrice(featured.price_40hc),
      })
    : t('meta.lane.titleEmpty')
  const desc = featured
    ? t('meta.lane.desc', {
        pol: cleanPort(featured.pol_name, featured.pol_code),
        polCode: featured.pol_code,
        pod: cleanPort(featured.pod_name, featured.pod_code),
        podCode: featured.pod_code,
        p20: fmtPrice(featured.price_20gp),
        p40: fmtPrice(featured.price_40hc),
        transit: featured.transit_days ?? '?',
        carrier: featured.carrier ?? t('meta.lane.carriersFallback'),
      })
    : t('meta.lane.descEmpty')
  usePageMeta(title, desc, `/rates/${slug.toLowerCase()}`, availableLocales)

  if (loading) return <Centered>{t('lane.loading')}</Centered>
  if (!featured) return <EmptyLane pol={pol} pod={pod} names={emptyNames} suggest={suggest} />

  const trade = tradeById(tradeBucket(featured.pod_code))
  const tradeShort = trade ? tradeName(trade, locale).short : ''

  return (
    <div className="hero-glow mx-auto max-w-6xl px-5 pt-10">
      <nav className="text-xs text-muted">
        <T to="/rates" className="hover:text-link">
          {t('lane.crumbLanes')}
        </T>
        {trade && (
          <>
            {' / '}
            <T to={`/trade/${trade.slug}`} className="hover:text-link">
              {tradeShort}
            </T>
          </>
        )}
      </nav>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span
              className="mono rounded-md px-2 py-0.5 text-[11px] font-semibold"
              style={{
                background: carrierBrand(featured.carrier).bg,
                color: carrierBrand(featured.carrier).fg,
              }}
            >
              {featured.carrier ?? '—'}
            </span>
            {trade && (
              <span className="mono text-[11px] uppercase tracking-wider text-muted">
                {tradeShort}
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2.5">
            <h1 className="font-display text-3xl font-light sm:text-5xl">
              {cleanPort(featured.pol_name, featured.pol_code)}{' '}
              <span className="text-faint">→</span>{' '}
              {cleanPort(featured.pod_name, featured.pod_code)}
            </h1>
            <StarButton
              lane={{
                pol: featured.pol_code,
                pod: featured.pod_code,
                polName: featured.pol_name,
                podName: featured.pod_name,
              }}
              className="h-9 w-9"
            />
          </div>
          <p className="mt-2 text-muted">
            {featured.pol_code} → {featured.pod_code} · {total} {t('lane.sailingsWord')} ·{' '}
            {t('lane.indicativeSpot')}, {t('lane.updated')} {fmtDate(updatedAt)}.
          </p>
          {rel[0] && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-surface-0 px-3 py-1 text-sm">
              <span className="mono text-[11px] uppercase tracking-wide text-faint">On-time</span>
              <OnTime pct={rel[0].on_time_pct} />
              <span className="text-xs text-muted">
                {rel[0].carrier} · {rel[0].observations} sailings tracked
                {rel[0].window_days ? ` · ${rel[0].window_days}d` : ''}
              </span>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="mono text-[11px] uppercase tracking-wider text-faint">
            {CTR_LABEL[ctrKey]} · spot
          </div>
          <div className="font-display text-5xl sm:text-6xl tnum text-gradient leading-none">
            {fmtPrice(priceForCtr(featured, ctrKey))}
          </div>
          {featured.transit_days && (
            <div className="mt-1 text-sm text-muted tnum">
              ~{featured.transit_days} {t('lane.daysTransit')}
            </div>
          )}
          <a
            href="#book"
            className="btn-accent mt-4 inline-block rounded-xl px-5 py-2.5 text-sm font-semibold"
          >
            {t('lane.getQuote')}
          </a>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        {/* min-w-0: without it a grid item defaults to min-width:auto and the wide schedule table
            (inside overflow-x-auto) pushes this column — and the whole page — past the phone width. */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <PriceCards s={featured} highlight={ctrKey} onPick={pickCtr} />
          <RateTrend history={history} current={featured.price_40hc} />
          <SameShipCompare sailings={sailings} onSelect={pickSailing} ctrKey={ctrKey} />
          <ReliabilityPanel rel={rel} sailings={sailings} />
          <SailingsTable
            sailings={sailings}
            rel={relByCarrier}
            onSelect={pickSailing}
            ctrKey={ctrKey}
          />
          <RoutingCard s={featured} />
          <LaneFacts s={featured} />
          <LaneInfo featured={featured} sailings={sailings} total={total} />
          <SponsorSlot context={`${featured.pol_name} → ${featured.pod_name}`} />
        </div>

        <aside
          id="book"
          className="scroll-mt-24 rounded-xl border border-signal/40 bg-surface-0 p-5 h-fit card-shadow lg:sticky lg:top-6"
        >
          <h2 className="font-display text-lg font-bold">{t('lane.book')}</h2>
          <p className="mt-1 text-sm text-muted">{t('lane.bookSub')}</p>
          <div className="mt-4">
            <RfqForm
              prefill={{
                pol_code: featured.pol_code,
                pol_name: cleanPort(featured.pol_name, featured.pol_code),
                pod_code: featured.pod_code,
                pod_name: cleanPort(featured.pod_name, featured.pod_code),
                container_type: selected?.container ?? ctrKey,
                carrier: selected?.carrier ?? undefined,
                vessel: selected?.vessel ?? undefined,
                voyage: selected?.voyage ?? undefined,
                etd: selected?.etd ?? undefined,
                sailing_price: selected?.price ?? undefined,
              }}
              onClearSelected={selected ? () => setSelected(null) : undefined}
            />
          </div>
          <div className="mt-4">
            <WatchLaneButton
              pol={featured.pol_code}
              pod={featured.pod_code}
              polName={cleanPort(featured.pol_name, featured.pol_code)}
              podName={cleanPort(featured.pod_name, featured.pod_code)}
            />
          </div>
        </aside>
      </div>
    </div>
  )
}

// Data-driven narrative + About + FAQ — matches the enriched prerendered body (SEO + user parity).
function LaneInfo({
  featured,
  sailings,
  total,
}: { featured: Sailing; sailings: Sailing[]; total: number }) {
  const t = useT()
  const carriers = [...new Set(sailings.map((s) => s.carrier).filter(Boolean))] as string[]
  const transits = sailings.map((s) => s.transit_days).filter((v): v is number => v != null)
  const cheapest = sailings
    .filter((s) => s.price_40hc != null)
    .sort((a, b) => (a.price_40hc ?? 0) - (b.price_40hc ?? 0))[0]
  const origin = cleanPort(featured.pol_name, featured.pol_code)
  const destination = cleanPort(featured.pod_name, featured.pod_code)
  const V: Record<string, string | number> = {
    origin,
    destination,
    n: total,
    carriers: carriers.length,
    tmin: transits.length ? Math.min(...transits) : '?',
    tmax: transits.length ? Math.max(...transits) : '?',
    fastest: transits.length ? Math.min(...transits) : '?',
    cheapest40: fmtPrice(cheapest?.price_40hc ?? null),
    cheapestCarrier: cheapest?.carrier ?? '—',
    p20: fmtPrice(featured.price_20gp),
    p40: fmtPrice(featured.price_40hc),
    transit: featured.transit_days ?? (transits.length ? Math.min(...transits) : '?'),
    carrierList: carriers.slice(0, 5).join(', ') || t('meta.lane.carriersFallback'),
  }
  const routing = sailings.some((s) => s.is_via) ? t('lane.routingTransship') : t('lane.routingDirect')
  const faq = [1, 2, 3, 4].map((i) => ({ q: t(`lane.faq.q${i}`, V), a: t(`lane.faq.a${i}`, V) }))
  return (
    <div className="space-y-6">
      <p className="text-muted leading-relaxed">{t('lane.narrative', { ...V, routing })}</p>
      <div>
        <h2 className="font-display text-lg font-bold">{t('lane.about')}</h2>
        <p className="mt-2 text-muted leading-relaxed">{t('lane.aboutBody', V)}</p>
      </div>
      <div>
        <h2 className="font-display text-lg font-bold">{t('lane.faqTitle')}</h2>
        <div className="mt-3 space-y-4">
          {faq.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-ink">{f.q}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function PriceCards({
  s,
  highlight,
  onPick,
}: { s: Sailing; highlight?: string; onPick: (key: string) => void }) {
  const t = useT()
  const ctn = s.raw?.ctnProduct ?? {}
  const rows: [string, string, number | null, number | null][] = [
    ['20ft', '20GP', ctn['20GP']?.basePrice ?? null, s.price_20gp],
    ['40ft', '40GP', ctn['40GP']?.basePrice ?? null, s.price_40gp],
    ['40ft HC', '40HC', ctn['40HC']?.basePrice ?? null, s.price_40hc],
  ]
  return (
    <div className="grid grid-cols-3 gap-3">
      {rows.map(([label, key, base, total]) => {
        const on = highlight === key
        return (
          <button
            type="button"
            key={key}
            onClick={() => onPick(key)}
            aria-pressed={on}
            className={`relative block w-full rounded-xl border bg-surface-0 p-4 text-left card-shadow transition-colors hover:border-signal ${
              on ? 'border-signal ring-1 ring-signal/40' : 'border-border-strong'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted">{label}</div>
              <span
                className={`mono text-[9px] uppercase tracking-wide ${on ? 'text-signal' : 'text-faint'}`}
              >
                {on ? t('lane.yourPick') : 'View'}
              </span>
            </div>
            <div className="mt-1 font-display text-2xl tnum text-signal">{fmtPrice(total)}</div>
            {base != null && total != null && base !== total && (
              <div className="mt-0.5 text-xs text-faint tnum">
                {t('lane.base')} {fmtPrice(base)}
              </div>
            )}
            <div className="mt-0.5 text-[11px] text-faint">{s.currency ?? 'USD'}</div>
          </button>
        )
      })}
    </div>
  )
}

/** Smart empty state — never a dead end. Explains, offers a quote, and suggests real lanes. */
function EmptyLane({
  pol,
  pod,
  names,
  suggest,
}: { pol?: string; pod?: string; names?: { pol?: string; pod?: string }; suggest: LaneRow[] }) {
  const polLabel = names?.pol ? cap(names.pol.toLowerCase()) : pol
  const podLabel = names?.pod ? cap(names.pod.toLowerCase()) : pod
  usePageMeta(
    'Ocean Freight Rate — FreightRatesMCP',
    'Request an ocean spot freight quote by lane, updated daily.',
    '/rates'
  )
  return (
    <div className="hero-glow mx-auto max-w-3xl px-5 pt-16 pb-24">
      <nav className="text-xs text-muted">
        <Link to="/rates" className="hover:text-link">
          Lanes
        </Link>
      </nav>
      <h1 className="mt-4 font-display text-3xl sm:text-4xl font-light">
        {pol && pod ? (
          <>
            {polLabel} <span className="text-faint">→</span> {podLabel}
          </>
        ) : (
          'That lane'
        )}
      </h1>
      <p className="mt-3 text-muted">
        We don't have a live published rate for this lane yet, but we can still get you a firm,
        all-in quotation from a vetted forwarder, usually within a working day. No cost, no
        obligation.
      </p>

      {suggest.length > 0 && (
        <div className="mt-8">
          <div className="mono text-[11px] uppercase tracking-wider text-faint">
            {pol ? `Live lanes from ${pol}` : 'Popular lanes'}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {suggest.map((l) => (
              <Link
                key={`${l.pol_code}-${l.pod_code}`}
                to={`/rates/${laneSlug(l.pol_code, l.pod_code)}`}
                className="rounded-full border border-border bg-surface-0 px-3 py-1.5 text-xs text-muted transition-colors hover:border-signal hover:text-ink"
              >
                {cap((l.pol_name || l.pol_code).toLowerCase())} →{' '}
                {cap((l.pod_name || l.pod_code).toLowerCase())}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 rounded-xl border border-signal/40 bg-surface-0 p-5 card-shadow">
        <h2 className="font-display text-lg font-bold">Get a quote for this lane</h2>
        <p className="mt-1 text-sm text-muted">
          Tell us the shipment; we match it to a vetted forwarder for a firm price.
        </p>
        <div className="mt-4">
          <RfqForm
            prefill={{
              pol_code: pol,
              pod_code: pod,
            }}
          />
        </div>
      </div>
    </div>
  )
}

// ── Same-ship comparison ──────────────────────────────────────────────────────
// Slot-sharing alliances put several carriers on ONE physical hull at DIFFERENT prices.
// The shared key across carriers is vessel name + ETD (voyage/service codes are per-carrier:
// COSCO sells EVER GIVEN's sailing as 028W/AEU9, CMA the identical hull as 0SCKXW1MA/FAL8).
// Grouping by hull surfaces "same ship, cheaper carrier" — often a four-figure saving.

const VESSEL_PLACEHOLDER = /^(vessel|tbn|tba|n\/?a|-+|\.+)$/i
const realVessel = (v: string | null | undefined): string | null => {
  const t = v?.trim()
  return t && !VESSEL_PLACEHOLDER.test(t) ? t : null
}

interface HullOffer {
  carrier: string
  price: number
  voyage: string | null
  transit: number | null
}
interface HullGroup {
  vessel: string
  etd: string | null
  offers: HullOffer[]
  cheapest: number
  dearest: number
  saving: number
}

// Slot-sharing carriers list the SAME vessel+voyage with ETDs a few days apart (feeder/cutoff
// reporting), and a vessel serves a given lane only ~once per 8–12-week rotation — so cards on one
// vessel within ~3 weeks are the same sailing. Cluster by that window rather than an exact ETD match.
const CLUSTER_WINDOW_MS = 21 * 864e5

/** Group sailings into per-vessel sailing clusters; keep only clusters sold by ≥2 carriers. Prices are
 *  for the chosen container (ctrKey) so the comparison follows the box the shopper picked. */
function sharedHulls(sailings: Sailing[], ctrKey: string): HullGroup[] {
  // 1. bucket every priced sailing by vessel
  const byVessel = new Map<string, Sailing[]>()
  for (const s of sailings) {
    const vessel = realVessel(s.vessel)
    if (!vessel || priceForCtr(s, ctrKey) == null || !s.carrier) continue
    const arr = byVessel.get(vessel.toUpperCase())
    if (arr) arr.push(s)
    else byVessel.set(vessel.toUpperCase(), [s])
  }

  const out: HullGroup[] = []
  for (const cards of byVessel.values()) {
    // 2. split a vessel's cards into sailing clusters by ETD gap (undated cards form one cluster)
    const dated = cards
      .filter((c) => c.etd)
      .sort((a, b) => Date.parse(a.etd as string) - Date.parse(b.etd as string))
    const clusters: Sailing[][] = []
    for (const c of dated) {
      const last = clusters[clusters.length - 1]
      const lastEtd = last?.[last.length - 1]?.etd
      if (last && lastEtd && Date.parse(c.etd as string) - Date.parse(lastEtd) <= CLUSTER_WINDOW_MS)
        last.push(c)
      else clusters.push([c])
    }
    const undated = cards.filter((c) => !c.etd)
    if (undated.length) clusters.push(undated)

    // 3. one comparison group per multi-carrier cluster (cheapest card wins per carrier)
    for (const cluster of clusters) {
      const offers: HullOffer[] = []
      for (const s of cluster) {
        const price = priceForCtr(s, ctrKey) as number
        const seen = offers.find((o) => o.carrier === s.carrier)
        if (seen) {
          if (price < seen.price) {
            seen.price = price
            seen.voyage = s.voyage
          }
        } else {
          offers.push({ carrier: s.carrier as string, price, voyage: s.voyage, transit: s.transit_days })
        }
      }
      if (new Set(offers.map((o) => o.carrier)).size < 2) continue
      offers.sort((a, b) => a.price - b.price)
      const first = offers[0]
      const last = offers[offers.length - 1]
      if (!first || !last) continue
      out.push({
        vessel: realVessel(cluster[0]?.vessel) ?? '',
        etd: cluster.find((c) => c.etd)?.etd ?? null,
        offers,
        cheapest: first.price,
        dearest: last.price,
        saving: last.price - first.price,
      })
    }
  }
  return out.sort((a, b) => b.saving - a.saving)
}

function SameShipCompare({
  sailings,
  onSelect,
  ctrKey,
}: { sailings: Sailing[]; onSelect: (s: SelectedSailing) => void; ctrKey: string }) {
  // Default to ETD (soonest sailing first) — the native expectation for a list of sailings, matching
  // the schedule table below; a shopper can flip to "Saving" to see the biggest opportunities first.
  const [sortBy, setSortBy] = useState<'etd' | 'saving'>('etd')
  const hulls = useMemo(() => {
    const arr = sharedHulls(sailings, ctrKey)
    if (sortBy === 'saving') return arr.sort((a, b) => b.saving - a.saving)
    return arr.sort((a, b) => {
      if (!a.etd && !b.etd) return 0
      if (!a.etd) return 1 // undated sinks to the bottom
      if (!b.etd) return -1
      return Date.parse(a.etd) - Date.parse(b.etd)
    })
  }, [sailings, sortBy, ctrKey])
  if (hulls.length === 0) return null
  const maxSaving = hulls.reduce((m, h) => Math.max(m, h.saving), 0)
  return (
    <div className="rounded-xl border border-signal/40 bg-surface-0 card-shadow overflow-hidden">
      <div className="px-5 pt-4 pb-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg font-bold">Same ship, cheaper freight</h2>
          {maxSaving > 0 && (
            <span className="mono rounded-full bg-signal-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-signal">
              save up to {fmtPrice(maxSaving)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <span className="mono text-[10px] uppercase tracking-wide text-faint">Sort</span>
            {(
              [
                ['etd', 'ETD'],
                ['saving', 'Saving'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setSortBy(k)}
                className={`mono rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide transition-colors ${
                  sortBy === k
                    ? 'bg-signal-soft font-semibold text-signal'
                    : 'text-muted hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-1 text-sm text-muted">
          Several carriers sell space on the <span className="text-ink">same vessel</span> —
          identical sailing, different price. Book the cheapest.
        </p>
      </div>
      <div className="mt-2">
        {hulls.map((h) => (
          <div
            key={`${h.vessel}-${h.etd}`}
            className="border-t border-border px-5 py-3 first:border-t-0"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-medium text-ink">
                {h.vessel}
                {h.etd && <span className="ml-2 text-xs text-faint tnum">ETD {fmtDate(h.etd)}</span>}
              </div>
              {h.saving > 0 && (
                <div className="shrink-0 text-xs font-semibold text-down tnum">
                  save {fmtPrice(h.saving)} · {Math.round((h.saving / h.dearest) * 100)}%
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {h.offers.map((o, idx) => {
                const brand = carrierBrand(o.carrier)
                const best = idx === 0 && h.saving > 0
                return (
                  <button
                    type="button"
                    key={o.carrier}
                    onClick={() =>
                      onSelect({
                        carrier: o.carrier,
                        vessel: h.vessel,
                        voyage: o.voyage,
                        etd: h.etd,
                        price: o.price,
                        container: ctrKey,
                      })
                    }
                    title={`Pre-book ${o.carrier} on ${h.vessel}: ${fmtPrice(o.price)}`}
                    className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm transition-colors ${
                      best
                        ? 'border-signal bg-signal-soft'
                        : 'border-border-strong hover:border-signal'
                    }`}
                  >
                    <span
                      className="mono rounded px-1.5 py-0.5 text-[10px] font-bold leading-none"
                      style={{ backgroundColor: brand.bg, color: brand.fg }}
                    >
                      {o.carrier}
                    </span>
                    <span className={`tnum font-semibold ${best ? 'text-signal' : 'text-ink'}`}>
                      {fmtPrice(o.price)}
                    </span>
                    {best && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-signal">
                        best
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Sortable sailing schedule ─────────────────────────────────────────────────
type SortKey = 'carrier' | 'voyage' | 'etd' | 'eta' | 'transit' | 'ontime' | 'price'
type SortDir = 'asc' | 'desc'
const COLUMNS: { key: SortKey; label: string; right?: boolean; defaultDir: SortDir }[] = [
  { key: 'carrier', label: 'Carrier · vessel', defaultDir: 'asc' },
  { key: 'voyage', label: 'Voyage', defaultDir: 'asc' },
  { key: 'etd', label: 'ETD', defaultDir: 'asc' },
  { key: 'eta', label: 'ETA', defaultDir: 'asc' },
  { key: 'transit', label: 'Transit', right: true, defaultDir: 'asc' },
  { key: 'ontime', label: 'On-time', right: true, defaultDir: 'desc' },
  { key: 'price', label: '40HC', right: true, defaultDir: 'asc' },
]

/** Sort value for a column — string for text, number for date/num, null = "missing" (always last). */
function sortVal(
  s: Sailing,
  rel: Map<string, LaneReliability>,
  key: SortKey,
  ctrKey: string
): string | number | null {
  switch (key) {
    case 'carrier':
      return `${s.carrier ?? ''} ${s.vessel ?? ''}`.trim().toLowerCase() || null
    case 'voyage':
      return s.voyage?.toLowerCase() ?? null
    case 'etd':
      return s.etd ? Date.parse(s.etd) : null
    case 'eta':
      return s.eta ? Date.parse(s.eta) : null
    case 'transit':
      return s.transit_days ?? null
    case 'ontime':
      return s.carrier ? (rel.get(s.carrier)?.on_time_pct ?? null) : null
    case 'price':
      return priceForCtr(s, ctrKey) ?? null
  }
}

/** Brass caret shown on the active column; a faint ⇅ hint appears on hover for the rest. */
function SortCaret({ active, dir }: { active: boolean; dir: SortDir }) {
  if (active)
    return <span className="text-signal">{dir === 'asc' ? '↑' : '↓'}</span>
  return <span className="text-faint opacity-0 transition-opacity group-hover:opacity-60">↕</span>
}

function SailingsTable({
  sailings,
  rel,
  onSelect,
  ctrKey,
}: {
  sailings: Sailing[]
  rel: Map<string, LaneReliability>
  onSelect: (s: SelectedSailing) => void
  ctrKey: string
}) {
  const [sortKey, setSortKey] = useState<SortKey>('etd')
  const [dir, setDir] = useState<SortDir>('asc')

  function onSort(col: (typeof COLUMNS)[number]) {
    if (col.key === sortKey) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(col.key)
      setDir(col.defaultDir)
    }
  }

  const sorted = useMemo(() => {
    const mul = dir === 'asc' ? 1 : -1
    return [...sailings].sort((a, b) => {
      const va = sortVal(a, rel, sortKey, ctrKey)
      const vb = sortVal(b, rel, sortKey, ctrKey)
      if (va === null && vb === null) return 0
      if (va === null) return 1 // missing always sinks to the bottom, either direction
      if (vb === null) return -1
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * mul
      return ((va as number) - (vb as number)) * mul
    })
  }, [sailings, rel, sortKey, dir, ctrKey])

  return (
    <div className="rounded-xl border border-border-strong bg-surface-0 card-shadow overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
        <div className="font-display text-lg font-bold">Sailing schedule</div>
        {/* Mobile only: the table becomes stacked cards below md, so surface a compact sort control. */}
        <select
          aria-label="Sort sailings"
          value={sortKey}
          onChange={(e) => {
            const col = COLUMNS.find((c) => c.key === e.target.value)
            if (col) {
              setSortKey(col.key)
              setDir(col.defaultDir)
            }
          }}
          className="mono rounded-md border border-border-strong bg-surface-0 px-2 py-1 text-xs text-muted md:hidden"
        >
          {COLUMNS.map((col) => (
            <option key={col.key} value={col.key}>
              {col.key === 'price' ? `Sort: ${ctrKey}` : `Sort: ${col.label}`}
            </option>
          ))}
        </select>
      </div>

      {/* Mobile (<md): stacked cards — the 8-column table can't fit a phone, so avoid the sideways scroll. */}
      <ul className="divide-y divide-border md:hidden">
        {sorted.map((s, i) => {
          const r = s.carrier ? rel.get(s.carrier) : undefined
          return (
            <li key={`m-${s.vessel}-${s.voyage}-${s.etd}-${i}`} className="px-5 py-3.5">
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0 font-medium">
                  <span className="text-ink">{s.carrier ?? '—'}</span>
                  {s.vessel && <span className="text-muted"> · {s.vessel}</span>}
                  {s.is_via && (
                    <span className="ml-1.5 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-faint">
                      via
                    </span>
                  )}
                </div>
                <div className="shrink-0 tnum font-semibold text-signal">
                  {fmtPrice(priceForCtr(s, ctrKey))}
                </div>
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-3 text-xs">
                <div className="tnum text-muted">
                  {fmtDate(s.etd)} → {fmtDate(s.eta)}
                  {s.transit_days ? ` · ${s.transit_days}d` : ''}
                </div>
                <div className="flex items-center gap-3">
                  {r ? <OnTime pct={r.on_time_pct} obs={r.observations} /> : null}
                  <button
                    type="button"
                    onClick={() =>
                      onSelect({
                        carrier: s.carrier,
                        vessel: s.vessel,
                        voyage: s.voyage,
                        etd: s.etd,
                        price: priceForCtr(s, ctrKey),
                        container: ctrKey,
                      })
                    }
                    className="rounded-md border border-border-strong px-2.5 py-1 font-medium text-muted transition-colors hover:border-signal hover:text-signal"
                  >
                    Book
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      {/* Desktop (md+): the full sortable table. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="bg-surface-1 text-left text-xs uppercase text-muted">
            <tr>
              {COLUMNS.map((col, i) => {
                const active = col.key === sortKey
                return (
                  <th
                    key={col.key}
                    aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`${i === 0 ? 'px-5' : col.key === 'price' ? 'pl-3 pr-6' : 'px-3'} py-2.5 font-medium ${col.right ? 'text-right' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => onSort(col)}
                      className={`group mono inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-ink ${
                        col.right ? 'flex-row-reverse' : ''
                      } ${active ? 'text-ink' : ''}`}
                    >
                      {col.key === 'price' ? ctrKey : col.label}
                      <SortCaret active={active} dir={dir} />
                    </button>
                  </th>
                )
              })}
              <th className="py-2.5 pl-2 pr-5 font-medium text-right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, i) => (
              <tr
                key={`${s.vessel}-${s.voyage}-${s.etd}-${i}`}
                className="group border-t border-border hover:bg-surface-1/50"
              >
                <td className="px-5 py-2.5">
                  <span className="text-ink">{s.carrier ?? '—'}</span>
                  {s.vessel && <span className="text-muted"> · {s.vessel}</span>}
                  {s.is_via && (
                    <span className="ml-2 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-faint">
                      via
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-muted tnum">{s.voyage ?? '—'}</td>
                <td className="px-3 py-2.5 tnum">{fmtDate(s.etd)}</td>
                <td className="px-3 py-2.5 tnum">{fmtDate(s.eta)}</td>
                <td className="px-3 py-2.5 text-right tnum">
                  {s.transit_days ? `${s.transit_days}d` : '—'}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {s.carrier && rel.get(s.carrier) ? (
                    <OnTime
                      pct={(rel.get(s.carrier) as LaneReliability).on_time_pct}
                      obs={(rel.get(s.carrier) as LaneReliability).observations}
                    />
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </td>
                <td className="py-2.5 pl-3 pr-6 text-right tnum text-signal">
                  {fmtPrice(priceForCtr(s, ctrKey))}
                </td>
                <td className="py-2.5 pl-2 pr-5 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      onSelect({
                        carrier: s.carrier,
                        vessel: s.vessel,
                        voyage: s.voyage,
                        etd: s.etd,
                        price: priceForCtr(s, ctrKey),
                        container: ctrKey,
                      })
                    }
                    className="rounded-md border border-border-strong px-2.5 py-1 text-xs font-medium text-muted transition-colors hover:border-signal hover:text-signal"
                  >
                    Book
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function RoutingCard({ s }: { s: Sailing }) {
  const legs = s.raw?.transferList ?? []
  return (
    <div className="rounded-xl border border-border-strong bg-surface-0 p-5 card-shadow">
      <h2 className="font-display text-lg font-bold">
        Routing {s.is_via ? '(transshipment)' : '(direct)'}
      </h2>
      {legs.length > 0 ? (
        <ol className="mt-4 space-y-4">
          {legs.map((leg, i) => (
            <LegItem
              key={`${leg.portName}-${i}`}
              leg={leg}
              first={i === 0}
              last={i === legs.length - 1}
            />
          ))}
        </ol>
      ) : (
        <p className="mt-2 text-sm text-muted">
          Direct {cleanPort(s.pol_name, s.pol_code)} → {cleanPort(s.pod_name, s.pod_code)}
          {s.vessel ? ` on ${s.vessel}${s.voyage ? ` ${s.voyage}` : ''}` : ''}.
        </p>
      )}
    </div>
  )
}

function LegItem({ leg, first, last }: { leg: TransshipLeg; first: boolean; last: boolean }) {
  const cutoffs: [string, number | null | undefined][] = [
    ['SI cutoff', leg.siCutOff],
    ['CY cutoff', leg.cyCutOff],
    ['Doc cutoff', leg.closeCutOff],
  ]
  const hasCutoffs = cutoffs.some(([, v]) => v)
  return (
    <li className="relative pl-6">
      <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-signal" />
      {!last && <span className="absolute left-[4px] top-4 bottom-[-16px] w-px bg-border-strong" />}
      <div className="font-medium text-ink">
        {leg.portName ?? '—'}
        {first && <span className="ml-2 text-[10px] uppercase text-faint">origin</span>}
        {last && <span className="ml-2 text-[10px] uppercase text-faint">destination</span>}
      </div>
      {leg.portPier && <div className="text-xs text-muted">{leg.portPier}</div>}
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
        {leg.vessel && (
          <span>
            Vessel <span className="text-ink">{leg.vessel}</span>
            {leg.voyage ? ` ${leg.voyage}` : ''}
          </span>
        )}
        {(leg.serviceCode || leg.serviceName) && (
          <span>Service {leg.serviceName || leg.serviceCode}</span>
        )}
        {leg.etd && <span>ETD {fmtDate(leg.etd)}</span>}
        {leg.eta && <span>ETA {fmtDate(leg.eta)}</span>}
      </div>
      {hasCutoffs && (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {cutoffs.map(([label, v]) =>
            v ? (
              <span
                key={label}
                className="rounded border border-border px-2 py-0.5 text-[11px] text-muted"
              >
                {label}: <span className="text-ink tnum">{fmtDateTime(v)}</span>
              </span>
            ) : null
          )}
        </div>
      )}
    </li>
  )
}

function LaneFacts({ s }: { s: Sailing }) {
  const facts: [string, string | null | undefined][] = [
    ['Place of receipt', s.por_code && s.por_code !== s.pol_code ? s.por_code : null],
    ['Origin port', `${cleanPort(s.pol_name, s.pol_code)} (${s.pol_code})`],
    ['Origin terminal', s.pol_terminal],
    ['Destination port', `${cleanPort(s.pod_name, s.pod_code)} (${s.pod_code})`],
    ['Destination terminal', s.pod_terminal],
    ['Place of delivery', s.pdl_code && s.pdl_code !== s.pod_code ? s.pdl_code : null],
    ['Service mode', s.service_mode],
    ['Service / route', s.route_code],
    ['Countries', [s.pol_country, s.pod_country].filter(Boolean).join(' → ') || null],
  ]
  return (
    <div className="rounded-xl border border-border-strong bg-surface-0 p-5 card-shadow">
      <h2 className="font-display text-lg font-bold">Lane details</h2>
      <dl className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
        {facts
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-border pb-2">
              <dt className="text-muted">{k}</dt>
              <dd className="text-ink text-right">{v}</dd>
            </div>
          ))}
      </dl>
      <p className="mt-4 text-xs text-faint">
        Compiled from public carrier listings and refreshed daily. Indicative ocean freight only —
        surcharges and local charges are confirmed on quotation.
      </p>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-2xl px-5 py-24 text-center text-muted">{children}</div>
}
